import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { SsoLink, SsoNote, providerFor } from '../lib/sso';

/**
 * The server answers identically whether or not the address has an account, so
 * this page can only ever show one success message. That is deliberate — a
 * different answer per address would turn this form into a way to discover who
 * holds an account.
 */
export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [ssoReady, setSsoReady] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    api<{ providers: string[] }>('/auth/providers')
      .then((r) => !cancelled && setSsoReady(r.providers))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  /*
    These addresses have no password here to reset — the provider holds the
    credential. Saying so beats letting someone submit and wait for an email
    that tells them the same thing.
  */
  const owner = providerFor(email, ssoReady);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    await api('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }).catch(() => {});
    setSubmitting(false);
    setSent(true);
  }

  if (sent) {
    return (
      <div className="mx-auto max-w-md px-4 py-24 text-center sm:px-6 lg:py-28">
        <h1 className="font-display text-2xl font-semibold tracking-tight text-ink-900">
          Check your email
        </h1>
        <p className="mt-4 text-ink-500">
          If that address has an account, a link to set a new password is on its way. It expires in
          an hour.
        </p>
        <Link
          to="/login"
          className="mt-9 inline-flex rounded-lg bg-ink-800 px-6 py-3 text-base font-medium text-white transition-colors hover:bg-ink-900"
        >
          Back to sign in
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto flex max-w-md flex-col px-4 py-16 sm:px-6 lg:py-24">
      <h1 className="font-display text-2xl font-semibold tracking-tight text-ink-900">
        Forgot your password
      </h1>
      <p className="mt-3 text-[0.9375rem] text-ink-500">
        Enter your email address and we'll send you a link to set a new one.
      </p>

      <form onSubmit={handleSubmit} className="mt-8 space-y-5" noValidate>
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-ink-900">
            Email address
          </label>
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-2 w-full rounded-lg border border-ink-200 bg-white px-4 py-3 text-base text-ink-900 transition-colors focus:border-gold-400 focus:outline-none focus:ring-2 focus:ring-gold-400/30"
          />
          {owner && (
            <SsoNote>
              {owner.label} looks after the password for this address, not us.{' '}
              <SsoLink provider={owner} /> on the sign-in page.
            </SsoNote>
          )}
        </div>

        <button
          type="submit"
          disabled={submitting || !!owner}
          className="w-full rounded-lg bg-ink-800 px-6 py-3.5 text-base font-medium text-white transition-colors hover:bg-ink-900 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {submitting ? 'Sending…' : 'Send reset link'}
        </button>
      </form>

      <Link
        to="/login"
        className="mt-8 text-center text-sm text-ink-400 transition-colors hover:text-ink-900"
      >
        ← Back to sign in
      </Link>
    </div>
  );
}
